import { useEffect, useState } from 'react';
import { Printer } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { toast } from '@/hooks/useToast';
import { ErrorBanner, expiryLabel } from '../borrow/ui';
import { shortDateTime } from '../outpatient/ui';
import { openTransferSlip } from './api';
import { TransferStatusBadge } from './TransferStatusBadge';
import { useCancelTransfer, useReceiveTransfer, useTransfer } from './useTransfers';

type Draft = Record<number, { qty: string; reason: string }>;

/**
 * One transfer: its lines, and — while it is in transit — Receive (enter what
 * actually arrived; a short line needs a reason) or Cancel (everything goes
 * back to the sending store). The printable slip is for both signatures.
 */
export default function TransferDetailDialog({ transferId, onClose }: { transferId: number; onClose: () => void }) {
  const query = useTransfer(transferId);
  const receive = useReceiveTransfer();
  const cancel = useCancelTransfer();
  const [mode, setMode] = useState<'view' | 'receive' | 'cancel'>('view');
  const [draft, setDraft] = useState<Draft>({});
  const [cancelReason, setCancelReason] = useState('');
  const [formError, setFormError] = useState('');
  const t = query.data;

  useEffect(() => {
    if (t) setDraft(Object.fromEntries(t.items.map((i) => [i.id, { qty: String(i.qty_issued), reason: '' }])));
  }, [t]);

  const submitReceive = () => {
    if (!t) return;
    setFormError('');
    const lines = [];
    for (const it of t.items) {
      const d = draft[it.id];
      const q = Number(d?.qty);
      if (!Number.isInteger(q) || q < 0 || q > it.qty_issued) return setFormError(`${it.item_name}: received must be 0 to ${it.qty_issued}.`);
      if (q < it.qty_issued && !d?.reason.trim()) return setFormError(`${it.item_name}: ${it.qty_issued - q} short — say what happened to them.`);
      lines.push({ item_id: it.id, qty_received: q, ...(q < it.qty_issued ? { discrepancy_reason: d!.reason.trim() } : {}) });
    }
    receive.mutate({ id: t.id, input: { lines } }, {
      onSuccess: (r) => { toast({ title: `${r.transfer_number} received into ${r.to_store_name}`, variant: 'success' }); setMode('view'); },
    });
  };

  const submitCancel = () => {
    if (!t) return;
    if (!cancelReason.trim()) return setFormError('Say why the transfer is being cancelled.');
    cancel.mutate({ id: t.id, reason: cancelReason.trim() }, {
      onSuccess: (r) => { toast({ title: `${r.transfer_number} cancelled — stock is back in ${r.from_store_name}`, variant: 'success' }); setMode('view'); },
    });
  };

  const printSlip = () => openTransferSlip(transferId).catch((e: Error) => toast({ title: 'Slip not opened', description: e.message, variant: 'danger' }));
  const serverError = (receive.error ?? cancel.error) instanceof Error ? (receive.error ?? cancel.error)!.message : null;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[820px]">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {t ? t.transfer_number : 'Transfer'} {t && <TransferStatusBadge status={t.status} />}
          </DialogTitle>
        </DialogHeader>
        {!t ? (
          <div className="p-4 text-[13px] text-muted-foreground">{query.isError ? 'Could not load this transfer.' : 'Loading…'}</div>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-2">
              <div><span className="text-muted-foreground">From</span> <b>{t.from_store_name}</b></div>
              <div><span className="text-muted-foreground">To</span> <b>{t.to_store_name}</b></div>
              <div><span className="text-muted-foreground">Issued</span> {shortDateTime(t.issued_at)} · {t.issued_by}</div>
              <div><span className="text-muted-foreground">Received</span> {t.received_at ? `${shortDateTime(t.received_at)} · ${t.received_by}` : '—'}</div>
              {t.notes && <div className="sm:col-span-2"><span className="text-muted-foreground">Notes</span> {t.notes}</div>}
              {t.cancel_reason && <div className="sm:col-span-2"><span className="text-muted-foreground">Cancelled</span> {shortDateTime(t.cancelled_at)} · {t.cancelled_by} — {t.cancel_reason}</div>}
            </div>

            <div className="overflow-x-auto">
              <Table className="min-w-[640px]">
                <TableHeader>
                  <TableRow>
                    {['Item', 'Batch', 'Expiry', 'Issued', mode === 'receive' ? 'Received' : 'Received', mode === 'receive' ? 'If short — why' : 'Note'].map((h, i) => <TableHead key={i}>{h}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {t.items.map((it) => {
                    const d = draft[it.id] ?? { qty: String(it.qty_issued), reason: '' };
                    const short = mode === 'receive' && Number(d.qty) < it.qty_issued;
                    return (
                      <TableRow key={it.id}>
                        <TableCell className="font-semibold">{it.item_name}</TableCell>
                        <TableCell className="font-mono text-xs">{it.batch_number}</TableCell>
                        <TableCell>{expiryLabel(it.expiry_date)}</TableCell>
                        <TableCell className="tabular-nums">{it.qty_issued}</TableCell>
                        <TableCell>
                          {mode === 'receive' ? (
                            <Input
                              type="number" min={0} max={it.qty_issued} inputMode="numeric" className="w-20"
                              aria-label={`Received of ${it.item_name}`}
                              value={d.qty} onChange={(e) => setDraft((x) => ({ ...x, [it.id]: { ...d, qty: e.target.value } }))}
                            />
                          ) : <span className="tabular-nums">{it.qty_received ?? '—'}</span>}
                        </TableCell>
                        <TableCell className="text-[12px]">
                          {mode === 'receive' ? (
                            short ? (
                              <Input value={d.reason} placeholder="e.g. 1 strip damaged"
                                onChange={(e) => setDraft((x) => ({ ...x, [it.id]: { ...d, reason: e.target.value } }))} />
                            ) : <span className="text-muted-foreground">—</span>
                          ) : (it.discrepancy_reason ?? (it.batch_selection_reason ? `Later batch: ${it.batch_selection_remarks || it.batch_selection_reason}` : ''))}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {mode === 'receive' && (
              <p className="text-[12px] text-muted-foreground">
                Count what actually arrived. Units not received are not returned to {t.from_store_name} — they are recorded as short on this transfer.
              </p>
            )}
            {mode === 'cancel' && (
              <FormField label="Why cancel?" hint={`Every unit goes back to ${t.from_store_name}.`}>
                <Textarea rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. Sent to the wrong store" />
              </FormField>
            )}
            <ErrorBanner message={formError || serverError} />
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" onClick={printSlip} disabled={!t}><Printer className="h-4 w-4" /> Print slip</Button>
          <span className="flex-1" />
          {t?.status === 'issued' && mode === 'view' && (
            <>
              <Button variant="ghost" onClick={() => { setFormError(''); setMode('cancel'); }}>Cancel transfer…</Button>
              <Button onClick={() => { setFormError(''); setMode('receive'); }}>Receive…</Button>
            </>
          )}
          {mode === 'receive' && (
            <>
              <Button variant="ghost" onClick={() => setMode('view')}>Back</Button>
              <Button onClick={submitReceive} loading={receive.isPending}>Confirm receipt</Button>
            </>
          )}
          {mode === 'cancel' && (
            <>
              <Button variant="ghost" onClick={() => setMode('view')}>Back</Button>
              <Button variant="danger" onClick={submitCancel} loading={cancel.isPending}>Cancel transfer</Button>
            </>
          )}
          {mode === 'view' && t?.status !== 'issued' && <Button variant="ghost" onClick={onClose}>Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
